import React, { useCallback, useEffect, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { addGoalsToGlobalGoalsMap } from 'actions/globalGoalsMapActions';
import { useDispatch } from 'react-redux';
import ExpandableAuditTable from 'screens/Initiatives/InitiativeAudit/ExpandableAuditTable';
import { ErrorMessage, Loader } from 'screens/analytics/commonComponents';

import NoDataImg from 'assets/assetsV1/illustrations/noGoals.svg';

import { ANALYTICS_TEXTS } from 'constants/ProjectConstants';
import { localStorageKeys } from 'constants/localStorageKeys';

import { getReviewPeriodSummaryInitiatives } from 'services/task';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { Paper } from 'uiToolkitV1/Paper';
import { TableBodyRow } from 'uiToolkitV1/TableComponents';

const defaultRowsPerPage = 10;

export const defaultRowsPerPageForGoals = {
  [localStorageKeys.rabbitReviewPeriodSummaryGoalsRowsPerPage]: defaultRowsPerPage,
};

const ROWS_PER_PAGE_OPTIONS = [10, 20, 30];

const getFilters = ({ startTS, endTS, employeeId }) => [
  {
    type: 'TYPE',
    operatorType: 'IN',
    data: ['TASK'],
  },
  {
    type: 'TASK_TYPE',
    operatorType: 'IN',
    data: ['SUB_TASK', 'TASK'],
  },
  {
    type: 'ACTIVE_CONTENT',
    operatorType: 'GREATER_THAN',
    data: [startTS],
  },
  {
    type: 'ACTIVE_CONTENT',
    operatorType: 'LESS_THAN',
    data: [endTS],
  },
  {
    type: 'NAME',
    operatorType: 'IN',
    data: [employeeId],
  },
];

const ReviewPeriodSummaryInitiatives = ({ startDate, endDate, employee }) => {
  const [page] = useState(0);
  const [, setCount] = useState(null);
  const [limit] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const dispatch = useDispatch();

  const [initiatives, setInitiatives] = useState([]);

  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [errorData, setErrorData] = useState(null);

  const [orderBy] = useState({
    columnId: null,
    orderType: 'ASC',
    filterTagType: null,
  });

  const fetchData = useCallback(() => {
    setNoData(false);
    setErrorData(false);

    setLoading(true);

    getReviewPeriodSummaryInitiatives({
      limit: 50,
      offset: 0,
      filters: getFilters({ startTS: startDate, endTS: endDate, employeeId: employee.id }),
    })
      .then((res) => {
        if (res.length === 0) {
          setNoData(true);
          return;
        }
        setCount(res.length);
        setInitiatives(res);

        dispatch(addGoalsToGlobalGoalsMap({ goals: res }));
      })
      .catch((err) => {
        console.error(err);
        setErrorData(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchData();
  }, [orderBy, page, limit, fetchData]);

  return (
    <Paper p={3}>
      {loading && <Loader />}
      {!loading && errorData && <ErrorMessage errorText={ANALYTICS_TEXTS.REVIEW_TABLE.ERROR_MSG} />}
      {!loading &&
        !errorData &&
        (noData ? (
          <Flex justifyContent="center">
            <div style={{ marginTop: '60px' }}>
              <EmptyScreen
                heading="You haven't created an Initiative"
                description="The best way to have a garden full of roses is to plant them! Take initiative and get started today!"
                illustrationSrc={NoDataImg}
              />
            </div>
          </Flex>
        ) : (
          <ExpandableAuditTable tableData={initiatives} hideEditActions />
        ))}
    </Paper>
  );
};

export const StyledTableBodyRow = styled(TableBodyRow)`
  .action {
    display: none;
  }
  &:hover {
    cursor: pointer;
    & > .info:not(:first-child) {
      display: none;
    }
    & > td:first-of-type .highlight-able-text {
      color: ${(props) => props.theme.colors.text['1565BC']};
    }
    & > .action {
      display: table-cell;
    }
  }
`;

export { ReviewPeriodSummaryInitiatives };
